import { useCallback, useEffect, useRef, useState } from "react";

import type { ScheduledQuestionnaireListItemDto } from "@/generated/model";
import { listScheduledQuestionnairesControllerListScheduledQuestionnaires } from "@/generated/scheduled-questionnaires/scheduled-questionnaires";
import { offlineDb } from "@/lib/offline/db";
import { syncService } from "@/lib/offline/syncService";

// Extended item type that includes sync status
export interface ScheduledQuestionnaireListItemWithSync extends ScheduledQuestionnaireListItemDto {
  // Local sync status - only set when there's unsynced local data
  localSyncStatus?: {
    isLocallyCompleted: boolean; // Completed locally but not yet synced
    pendingCount: number; // Number of pending sync items
    failedCount: number; // Number of failed sync items
  };
}

interface UseOfflineScheduledQuestionnairesResult {
  items: ScheduledQuestionnaireListItemWithSync[];
  isLoading: boolean;
  isOffline: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

const CACHE_KEY_PREFIX = "checklists-";

/**
 * Offline-first hook for fetching scheduled questionnaires.
 *
 * Flow:
 * 1. Immediately return cached data from IndexedDB (if available)
 * 2. If online, fetch fresh data from server in background
 * 3. Merge with local completion state (for unsynced items)
 * 4. Update UI with merged data
 *
 * Status Priority:
 * - Server status is authoritative (if server says COMPLETED, trust it)
 * - Local completion state only adds "syncing" indicator for unsynced items
 */
export function useOfflineScheduledQuestionnaires(
  nurseryId: string | null,
): UseOfflineScheduledQuestionnairesResult {
  const [items, setItems] = useState<ScheduledQuestionnaireListItemWithSync[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isOffline, setIsOffline] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Track if we've completed initial load to prevent re-triggering
  const initializedRef = useRef(false);
  const currentNurseryIdRef = useRef(nurseryId);

  const cacheKey = nurseryId ? `${CACHE_KEY_PREFIX}${nurseryId}` : null;

  // Merge server/cached items with local completion state
  const mergeWithLocalState = useCallback(
    async (serverItems: ScheduledQuestionnaireListItemDto[]): Promise<ScheduledQuestionnaireListItemWithSync[]> => {
      if (serverItems.length === 0) return [];

      const instanceIds = serverItems.map((item) => item.id);

      // Get local completions for these items
      const localCompletions = await offlineDb.completions
        .filter((c) => instanceIds.includes(c.instanceId))
        .toArray();

      const localCompletionMap = new Map(
        localCompletions.map((c) => [c.instanceId, c])
      );

      // Get sync status for these items
      const syncStatusMap = await syncService.getSyncStatusForItems(instanceIds);

      // Merge data
      return serverItems.map((item) => {
        const localCompletion = localCompletionMap.get(item.id);
        const syncStatus = syncStatusMap.get(item.id);

        // Check if locally completed but server doesn't know yet
        const isLocallyCompleted =
          item.status !== "COMPLETED" && item.status !== "COMPLETED_LATE" &&
          localCompletion?.completedAt !== null &&
          localCompletion?.completedAt !== undefined;

        // Only add localSyncStatus if there's something to show
        if (isLocallyCompleted || (syncStatus && (syncStatus.pending > 0 || syncStatus.failed > 0))) {
          return {
            ...item,
            localSyncStatus: {
              isLocallyCompleted,
              pendingCount: syncStatus?.pending || 0,
              failedCount: syncStatus?.failed || 0,
            },
          };
        }

        return item;
      });
    },
    []
  );

  // Load cached data from IndexedDB
  const loadFromCache = useCallback(async (): Promise<ScheduledQuestionnaireListItemDto[] | null> => {
    if (!cacheKey) return null;

    try {
      const cached = await offlineDb.checklists.get(cacheKey);
      if (cached) {
        return cached.items as unknown as ScheduledQuestionnaireListItemDto[];
      }
    } catch (err) {
      console.error("Failed to load from cache:", err);
    }
    return null;
  }, [cacheKey]);

  // Save data to IndexedDB cache
  const saveToCache = useCallback(
    async (data: ScheduledQuestionnaireListItemDto[]) => {
      if (!cacheKey || !nurseryId) return;

      try {
        await offlineDb.checklists.put({
          id: cacheKey,
          instanceId: nurseryId,
          title: "Scheduled Questionnaires",
          templateName: "",
          templateDescription: null,
          items: data as unknown as Array<{
            id: string;
            label: string;
            category?: string;
          }>,
          dueDate: null,
          cachedAt: Date.now(),
        });
      } catch (err) {
        console.error("Failed to save to cache:", err);
      }
    },
    [cacheKey, nurseryId]
  );

  // Fetch fresh data from server
  const fetchFromServer = useCallback(async (): Promise<ScheduledQuestionnaireListItemDto[] | null> => {
    if (!nurseryId) return null;

    const response = await listScheduledQuestionnairesControllerListScheduledQuestionnaires(
      nurseryId,
      { checklistView: true, includeAnswers: true }
    );

    return ((response?.data as { items?: ScheduledQuestionnaireListItemDto[] })?.items) || [];
  }, [nurseryId]);

  // Main refetch function - attempts server fetch
  const refetch = useCallback(async () => {
    if (!nurseryId || !navigator.onLine) return;

    try {
      const freshData = await fetchFromServer();
      if (freshData) {
        const mergedData = await mergeWithLocalState(freshData);
        setItems(mergedData);
        await saveToCache(freshData);
      }
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Failed to fetch"));
    }
  }, [nurseryId, fetchFromServer, saveToCache, mergeWithLocalState]);

  // Refresh local sync status without server fetch
  const refreshLocalState = useCallback(async () => {
    if (items.length === 0) return;

    // Re-merge current items with updated local state
    const baseItems = items.map((item) => {
      // Strip localSyncStatus to get base item
      const { localSyncStatus: _, ...baseItem } = item;
      return baseItem as ScheduledQuestionnaireListItemDto;
    });

    const mergedData = await mergeWithLocalState(baseItems);
    setItems(mergedData);
  }, [items, mergeWithLocalState]);

  // Initial load effect
  useEffect(() => {
    // Reset if nurseryId changes
    if (currentNurseryIdRef.current !== nurseryId) {
      currentNurseryIdRef.current = nurseryId;
      initializedRef.current = false;
    }

    if (initializedRef.current) return;

    let isMounted = true;

    async function initialize() {
      if (!nurseryId) {
        // Keep loading state true while waiting for nurseryId
        // This prevents flashing "no checklists" before data loads
        return;
      }

      // Check offline status after mount (avoid SSR mismatch)
      const offline = !navigator.onLine;
      setIsOffline(offline);

      // Step 1: Load cached data immediately
      const cachedItems = await loadFromCache();

      if (!isMounted) return;

      if (cachedItems && cachedItems.length > 0) {
        // We have cached data - merge with local state and show immediately
        const mergedData = await mergeWithLocalState(cachedItems);
        if (isMounted) {
          setItems(mergedData);
          setIsLoading(false);
        }
      }

      // Step 2: If online, fetch fresh data in background
      if (!offline) {
        try {
          const freshData = await fetchFromServer();
          if (freshData && isMounted) {
            const mergedData = await mergeWithLocalState(freshData);
            setItems(mergedData);
            await saveToCache(freshData);
          }
          if (isMounted) setError(null);
        } catch (err) {
          if (isMounted) {
            // Only set error if we don't have any cached data
            if (!cachedItems || cachedItems.length === 0) {
              setError(err instanceof Error ? err : new Error("Failed to fetch"));
            }
          }
        }
      }

      if (isMounted) {
        setIsLoading(false);
        initializedRef.current = true;
      }
    }

    initialize();

    return () => {
      isMounted = false;
    };
  }, [nurseryId, loadFromCache, fetchFromServer, saveToCache, mergeWithLocalState]);

  // Online/offline listeners
  useEffect(() => {
    const handleOnline = () => {
      setIsOffline(false);
      refetch();
    };

    const handleOffline = () => {
      setIsOffline(true);
    };

    // Set initial state after mount
    setIsOffline(!navigator.onLine);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [refetch]);

  // Refs to hold latest callbacks - this prevents subscription from being recreated
  const refetchRef = useRef(refetch);
  const refreshLocalStateRef = useRef(refreshLocalState);

  // Keep refs up to date
  useEffect(() => {
    refetchRef.current = refetch;
  }, [refetch]);

  useEffect(() => {
    refreshLocalStateRef.current = refreshLocalState;
  }, [refreshLocalState]);

  // Track previous pending count in a ref to persist across re-renders
  const previousPendingCountRef = useRef(0);

  // Subscribe to sync service to refetch/refresh when sync status changes
  // Using refs ensures this effect only runs once on mount
  useEffect(() => {
    const unsubscribe = syncService.subscribe((status) => {
      const previousPendingCount = previousPendingCountRef.current;

      // Refetch from server when sync completes (pendingCount goes from >0 to 0)
      if (previousPendingCount > 0 && status.pendingCount === 0) {
        console.log("[OfflineScheduledQuestionnaires] Sync complete - refetching from server");
        refetchRef.current();
      } else if (status.pendingCount !== previousPendingCount) {
        // Just refresh local state to update sync indicators
        console.log("[OfflineScheduledQuestionnaires] Sync status changed - refreshing local state");
        refreshLocalStateRef.current();
      }

      previousPendingCountRef.current = status.pendingCount;
    });

    return () => {
      unsubscribe();
    };
  }, []); // Empty deps - subscribe once on mount

  return {
    items,
    isLoading,
    isOffline,
    error,
    refetch,
  };
}
